import { Dispatch, FocusEvent, KeyboardEvent, RefObject, SetStateAction, useState } from 'react';

interface UseDropdownKeyboardNavigationParams {
  isOpen: boolean;
  setIsOpen: Dispatch<SetStateAction<boolean>>;
  toggleClick?: boolean;
  itemCount: number;
  anchorRef: RefObject<HTMLElement | null>;
  listRef: RefObject<HTMLUListElement | null>;
}

/**
 * Keyboard handling for DropdownTemplate: Enter/Space toggle, arrow-key roving focus, Escape/Tab/blur dismissal
 * @param isOpen - current open state
 * @param setIsOpen - open state setter
 * @param toggleClick - when true the anchor's own click already toggles, so Enter/Space skip toggling
 * @param itemCount - number of dropdown items
 * @param anchorRef - ref to the trigger element
 * @param listRef - ref to the list element whose children are the items
 */
export const useDropdownKeyboardNavigation = ({
  isOpen,
  setIsOpen,
  toggleClick,
  itemCount,
  anchorRef,
  listRef,
}: UseDropdownKeyboardNavigationParams) => {
  const [current, setCurrent] = useState(-1);

  const focusItem = (index: number) => {
    const li = listRef.current?.children?.[index] as HTMLElement | undefined;
    if (!li) return;
    (li.querySelector<HTMLElement>('a[href]') ?? li).focus();
  };

  const isFromItemLink = (e: KeyboardEvent) => {
    const target = e.target as HTMLElement;
    return !!listRef.current?.contains(target) && !!target.closest('a[href]');
  };

  const onKeyDown = (e: KeyboardEvent<HTMLDivElement>) => {
    if (e.code === 'Enter' || e.code === 'Space') {
      // Item links handle their own activation keys
      if (!isFromItemLink(e)) e.preventDefault();
      if (e.target === anchorRef.current) {
        // With toggleClick, NavItem's keydown already triggers the toggling click
        if (!toggleClick) setIsOpen(prevState => !prevState);
      } else {
        setIsOpen(false);
      }
    }

    if (e.key === 'Escape' && isOpen) {
      e.preventDefault();
      anchorRef.current?.focus();
      setIsOpen(false);
    }

    if (e.key === 'Tab' && isOpen) {
      setIsOpen(false);
    }

    if (e.key === 'ArrowDown' && isOpen) {
      e.preventDefault();
      const next = current < 0 || current + 1 === itemCount ? 0 : current + 1;
      focusItem(next);
      setCurrent(next);
    }

    if (e.key === 'ArrowUp' && isOpen) {
      e.preventDefault();
      const prev = current <= 0 ? itemCount - 1 : current - 1;
      focusItem(prev);
      setCurrent(prev);
    }
  };

  const onBlur = (e: FocusEvent) => {
    if (!e.currentTarget.contains(e.relatedTarget)) {
      setTimeout(() => {
        if (isOpen) setIsOpen(false);
      }, 10);
    }
    setCurrent(-1);
  };

  return { onKeyDown, onBlur };
};
